import { useState, useRef } from 'react';
import classNames from 'classnames';

import BrIcons from '@components/BrIcons';
import BrOutsideAlerter from '@components/BrOutsideAlerter';
import './BrSelect.scss';
import BrText from '@components/BrText';

interface Option {
  title?: any;
  value?: any;
  group?: any;
  [key: string | number]: any;
}
interface BrSelectProps {
  options: Array<Option>;
  placeholder?: string;
  onSelect?: any;
  selected?: any;
  allowSearch?: boolean;
  isReturnWholeItem?: boolean;
  isDisabled?: boolean;
  hasGroupedOptions?: boolean;
  searchPlaceholder?: string;
  openToLeft?: boolean;
  hideButtonBorder?: boolean;
}
const BrSelect = (props: BrSelectProps) => {
  const {
    options,
    placeholder,
    onSelect,
    selected,
    allowSearch,
    isReturnWholeItem,
    isDisabled,
    hasGroupedOptions,
    searchPlaceholder,
    openToLeft,
    hideButtonBorder,
  } = props;

  const dropdownRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [highlightedValue, setHighlightedValue] = useState('');
  const getSelectedTitle = () => {
    const valueToFind = isReturnWholeItem && selected ? selected.value : selected;
    // eslint-disable-next-line eqeqeq
    const option = options.find((option: any) => option.value == valueToFind);
    return option && option.title;
  };

  const inputChangeHandler = (e: any) => {
    setInputVal(e.target.value);
  };

  const _className = classNames('br-select', { active: isActive }, { disabled: isDisabled });

  const itemSelectHandler = (item: any) => {
    if (item.disabled) {
      return false;
    }
    setInputVal('');
    onSelect(isReturnWholeItem ? item : item.value);
    setIsActive(false);
  };

  const itemsToShow = (options: any) => {
    return options.filter((option: any) => {
      if (!allowSearch) {
        return true;
      }
      if (!inputVal || inputVal.trim().length === 0) {
        return true;
      }
      return option.searchableTitle
        ? option.searchableTitle.toLowerCase().includes(inputVal.toLowerCase())
        : option.title.toLowerCase().includes(inputVal.toLowerCase());
    });
  };

  const handleListItemMouseOver = (value: string) => {
    setHighlightedValue(value);
  };

  const renderOptions = (options: any) => {
    return itemsToShow(options).map((item: any) => {
      const listItemClassNames = classNames('list-item', { hovered: item.value === highlightedValue });

      return (
        <div
          className={listItemClassNames}
          key={item.value}
          onClick={() => itemSelectHandler(item)}
          onMouseOver={() => handleListItemMouseOver(item.value)}
          onFocus={() => handleListItemMouseOver(item.value)}
        >
          <BrText>{item.title}</BrText>
        </div>
      );
    });
  };

  const renderGroupedOptions = (groupedOptions: any) => {
    return Object.keys(groupedOptions).map((key: string) => {
      if (groupedOptions[key].length === 0 || itemsToShow(groupedOptions[key]).length === 0) {
        return <></>;
      }

      return (
        <>
          <div className="list-item-title">{key === '__default' ? '' : key}</div>
          {renderOptions(groupedOptions[key])}
        </>
      );
    });
  };
  const groupedOptions: any = { __default: [] };
  if (hasGroupedOptions) {
    options.forEach((option) => {
      if (option.group) {
        if (!groupedOptions[option.group]) {
          groupedOptions[option.group] = [];
        }
        groupedOptions[option.group].push(option);
      } else {
        groupedOptions.__default.push(option);
      }
    });
  }
  const handleOutsideClick = (): void => {
    setIsActive(false);
    setInputVal('');
  };

  const dropdownClassNames = classNames('list', { 'open-to-left': openToLeft });
  const buttonClassNames = classNames('text', { 'hide-button-border': hideButtonBorder });

  return (
    <BrOutsideAlerter callbackFunc={handleOutsideClick}>
      <div ref={dropdownRef} className={_className}>
        <div className={buttonClassNames}>
          <div
            className="title-container"
            onClick={() => {
              setIsActive((currState) => !currState);
            }}
          >
            <div className="title">{getSelectedTitle() || placeholder}</div>
            <BrIcons.DownCaret isSubComp />
          </div>
        </div>
        <div className={dropdownClassNames}>
          {allowSearch && (
            <input
              autoCapitalize="none"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              tabIndex={0}
              onChange={inputChangeHandler}
              type="text"
              placeholder={searchPlaceholder || 'Search...'}
              value={inputVal}
              aria-autocomplete="list"
              autoFocus
              className="br-select-search-input"
            />
          )}
          {hasGroupedOptions ? renderGroupedOptions(groupedOptions) : renderOptions(options)}
        </div>
      </div>
    </BrOutsideAlerter>
  );
};

export default BrSelect;
